import type { AxiosResponse } from "axios";

const LOCAL_WRITE_TTL_MS = 15_000;

export type LiveEntityType =
	| "Job"
	| "Ticket"
	| "Asset"
	| "Record"
	| "JobType"
	| "TicketType"
	| "AssetType"
	| "RecordType"
	| "StatusLifecycle";

interface LocalWrite {
	entityType: LiveEntityType;
	entityId: string;
	expiresAt: number;
}

const writes = new Map<string, LocalWrite>();

function key(entityType: string, entityId: string): string {
	return `${entityType}:${entityId.toLowerCase()}`;
}

export function markLocalEntityWrite(
	entityType: LiveEntityType,
	entityId: string,
	now = Date.now(),
): void {
	writes.set(key(entityType, entityId), {
		entityType,
		entityId,
		expiresAt: now + LOCAL_WRITE_TTL_MS,
	});
}

export function hasRecentLocalEntityWrite(
	entityType: string,
	entityId: string,
	now = Date.now(),
): boolean {
	const write = writes.get(key(entityType, entityId));
	if (!write) return false;
	if (write.expiresAt <= now) {
		writes.delete(key(entityType, entityId));
		return false;
	}
	return true;
}

export function clearLocalEntityWrites(): void {
	writes.clear();
}

function entityTypeFromPlural(plural: string, isType: boolean): LiveEntityType {
	const singular = plural.slice(0, -1);
	const entity = `${singular[0]?.toUpperCase()}${singular.slice(1)}`;
	return `${entity}${isType ? "Type" : ""}` as LiveEntityType;
}

function responseEntityId(data: unknown): string | null {
	if (!data || typeof data !== "object") return null;
	const value = data as Record<string, unknown>;
	for (const candidate of [
		value.id,
		value.Id,
		(value.data as Record<string, unknown> | undefined)?.id,
		(value.object as Record<string, unknown> | undefined)?.id,
	]) {
		if (typeof candidate === "string" && candidate.length > 0) return candidate;
	}
	return null;
}

export function getLocalWriteFromResponse(
	response: AxiosResponse,
): { entityType: LiveEntityType; entityId: string } | null {
	const method = response.config.method?.toUpperCase();
	if (!method || !["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
		return null;
	}

	const pathname = new URL(
		response.config.url ?? "",
		response.config.baseURL ?? "http://localhost",
	).pathname.replace(/\/+$/, "");

	const lifecycle = pathname.match(/\/status-lifecycles\/([^/]+)$/i);
	if (lifecycle) {
		return { entityType: "StatusLifecycle", entityId: lifecycle[1] };
	}

	const exact = pathname.match(
		/\/(jobs|tickets|assets|records)(\/types)?\/([^/]+)$/i,
	);
	if (exact) {
		return {
			entityType: entityTypeFromPlural(exact[1].toLowerCase(), !!exact[2]),
			entityId: exact[3],
		};
	}

	const create = pathname.match(/\/(jobs|tickets|assets|records)(\/types)?$/i);
	if (method === "POST" && create) {
		const entityId = responseEntityId(response.data);
		if (!entityId) return null;
		return {
			entityType: entityTypeFromPlural(create[1].toLowerCase(), !!create[2]),
			entityId,
		};
	}

	return null;
}

export function markLocalEntityWriteFromResponse(response: AxiosResponse): void {
	const write = getLocalWriteFromResponse(response);
	if (write) markLocalEntityWrite(write.entityType, write.entityId);
}
